import { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MessageSquare, Video, Search, Send, Paperclip, Users,
  Phone, CalendarDays, Clock, Plus, ChevronRight, User,
  Hash, ArrowRight, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  threadsComunicazioni, messaggiThread, teleconsultiProgrammati,
} from '@/data/mockMMGData';
import { pazientiInCaricoMMG } from '@/data/mockMMGData';

const tabs = [
  { id: 'chat', label: 'Messaggistica Sicura', icon: MessageSquare },
  { id: 'teleconsulti', label: 'Teleconsulti e Videochiamate', icon: Video },
];

const tipoLabel = {
  canale: { label: 'Canale', color: '#3B82F6' },
  equipe: { label: 'Equipe', color: '#06B6D4' },
  diretto: { label: 'Diretto', color: '#8B5CF6' },
};

const statoTeleconsulto = {
  confermato: { label: 'Confermato', bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  'da-confermare': { label: 'Da Confermare', bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
};

/* ══════ TAB SELECTOR ══════ */
function TabSelector({ active, onChange }) {
  return (
    <div className="flex gap-1 p-1 bg-muted/30 rounded-2xl border border-border/20 w-fit" data-testid="tab-selector">
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${isActive ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${t.id}`}
          >
            <Icon className="w-4 h-4" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ══════ LISTA CHAT (Colonna SX) ══════ */
function ChatList({ threads, activeId, onSelect, searchQuery, onSearch }) {
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return threads;
    const q = searchQuery.toLowerCase();
    return threads.filter(t => t.titolo.toLowerCase().includes(q) || t.ultimoMessaggio.toLowerCase().includes(q));
  }, [threads, searchQuery]);

  return (
    <div className="flex flex-col h-full border-r border-border/20" data-testid="chat-list-panel">
      {/* Search */}
      <div className="p-3 border-b border-border/20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-border/40 bg-muted/20 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-card"
            placeholder="Cerca conversazioni..."
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            data-testid="chat-search-input"
          />
        </div>
      </div>

      {/* Threads */}
      <div className="flex-1 overflow-y-auto" data-testid="chat-thread-list">
        {filtered.map((t) => {
          const isActive = activeId === t.id;
          const tipo = tipoLabel[t.tipo] || tipoLabel.diretto;
          return (
            <button
              key={t.id}
              onClick={() => onSelect(t.id)}
              className={`w-full flex items-start gap-3 px-4 py-3.5 text-left transition-colors border-b border-border/10 ${isActive ? 'bg-primary/[0.06]' : 'hover:bg-muted/20'}`}
              data-testid={`chat-thread-${t.id}`}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                style={{ backgroundColor: t.color }}
              >
                {t.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-xs font-semibold truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>{t.titolo}</span>
                  {t.nonLetti > 0 && (
                    <span className="ml-auto flex-shrink-0 w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid={`unread-badge-${t.id}`}>
                      {t.nonLetti}
                    </span>
                  )}
                </div>
                <p className={`text-[11px] truncate ${t.nonLetti > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  {t.ultimoMessaggio}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="text-[8px] border-0 px-1.5 py-0" style={{ backgroundColor: `${tipo.color}12`, color: tipo.color }}>{tipo.label}</Badge>
                  <span className="text-[9px] text-muted-foreground">{t.timestamp.split(' ')[1]}</span>
                </div>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10">Nessuna conversazione trovata.</p>
        )}
      </div>
    </div>
  );
}

/* ══════ FINESTRA CONVERSAZIONE (Colonna DX) ══════ */
function ChatWindow({ thread, messages, onSend }) {
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (!thread) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="chat-empty-state">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-muted/30 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-7 h-7 text-muted-foreground/40" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">Seleziona una conversazione</p>
          <p className="text-[11px] text-muted-foreground/60 mt-1">Scegli un canale o contatto dalla lista a sinistra</p>
        </div>
      </div>
    );
  }

  const tipo = tipoLabel[thread.tipo] || tipoLabel.diretto;

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input.trim());
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full" data-testid="chat-window">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border/20 flex-shrink-0" data-testid="chat-window-header">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
          style={{ backgroundColor: thread.color }}
        >
          {thread.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{thread.titolo}</p>
          <p className="text-[10px] text-muted-foreground">
            {thread.partecipanti.join(', ')}
          </p>
        </div>
        <Badge className="text-[9px] border-0" style={{ backgroundColor: `${tipo.color}12`, color: tipo.color }}>
          {thread.tipo === 'canale' && <Hash className="w-2.5 h-2.5 mr-1" />}
          {thread.tipo === 'equipe' && <Users className="w-2.5 h-2.5 mr-1" />}
          {thread.tipo === 'diretto' && <User className="w-2.5 h-2.5 mr-1" />}
          {tipo.label}
        </Badge>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" ref={scrollRef} data-testid="chat-messages-area">
        {messages.map((m, i) => {
          const showAvatar = i === 0 || messages[i - 1]?.autore !== m.autore;
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`flex gap-2.5 ${m.mio ? 'flex-row-reverse' : ''}`}
              data-testid={`message-${m.id}`}
            >
              {showAvatar && !m.mio ? (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[9px] font-bold text-slate-500 dark:text-slate-300">
                    {m.autore.split(' ').slice(-1)[0]?.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              ) : (
                <div className="w-8 flex-shrink-0" />
              )}
              <div className={`max-w-[70%] ${m.mio ? 'items-end' : 'items-start'}`}>
                {showAvatar && (
                  <div className={`flex items-center gap-2 mb-1 ${m.mio ? 'justify-end' : ''}`}>
                    <span className="text-[10px] font-semibold text-foreground">{m.autore}</span>
                    <span className="text-[9px] text-muted-foreground">{m.ruolo}</span>
                  </div>
                )}
                <div className={`px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${m.mio ? 'bg-primary text-primary-foreground rounded-tr-md' : 'bg-muted/40 text-foreground rounded-tl-md'}`}>
                  {m.testo}
                </div>
                <p className={`text-[9px] text-muted-foreground mt-1 ${m.mio ? 'text-right' : ''}`}>{m.timestamp}</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-border/20 flex-shrink-0" data-testid="chat-input-area">
        <div className="flex items-end gap-2">
          <button
            className="w-10 h-10 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-muted/30 transition-colors flex-shrink-0"
            data-testid="btn-attach"
          >
            <Paperclip className="w-4.5 h-4.5" />
          </button>
          <div className="flex-1 relative">
            <textarea
              className="w-full min-h-[42px] max-h-32 rounded-xl border border-border/40 bg-card px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Scrivi un messaggio..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              data-testid="chat-message-input"
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="btn-send-message"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════ TAB TELECONSULTI ══════ */
const specialistiDisponibili = [
  'Cardiologia', 'Pneumologia', 'Geriatria', 'Neurologia',
  'Fisiatria', 'Diabetologia', 'Nefrologia', 'Oncologia',
];

function NuovoTeleconsultoModal({ onClose }) {
  const [paziente, setPaziente] = useState('');
  const [specialista, setSpecialista] = useState('');
  const [data, setData] = useState('');
  const [ora, setOra] = useState('');

  const canSubmit = paziente && specialista && data && ora;

  const handleSubmit = () => {
    const pName = pazientiInCaricoMMG.find(p => String(p.id) === paziente)?.nome || '';
    toast.success('Richiesta teleconsulto inviata', { description: `Teleconsulto ${specialista} per ${pName} il ${data} alle ${ora}.` });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="teleconsulto-modal-backdrop">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-card rounded-2xl shadow-xl border border-border/40 w-full max-w-md mx-4 p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="teleconsulto-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">Richiedi Teleconsulto</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted/40 transition-colors" data-testid="teleconsulto-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-foreground mb-1.5 block">Paziente</label>
            <select
              value={paziente}
              onChange={(e) => setPaziente(e.target.value)}
              className="w-full h-10 rounded-xl border border-border/40 bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="select-paziente"
            >
              <option value="">Seleziona paziente...</option>
              {pazientiInCaricoMMG.map(p => (
                <option key={p.id} value={p.id}>{p.nome} — {p.eta} anni</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-foreground mb-1.5 block">Specialista</label>
            <select
              value={specialista}
              onChange={(e) => setSpecialista(e.target.value)}
              className="w-full h-10 rounded-xl border border-border/40 bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="select-specialista"
            >
              <option value="">Seleziona specialista...</option>
              {specialistiDisponibili.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-foreground mb-1.5 block">Data</label>
              <input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                className="w-full h-10 rounded-xl border border-border/40 bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="input-data"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground mb-1.5 block">Ora</label>
              <input
                type="time"
                value={ora}
                onChange={(e) => setOra(e.target.value)}
                className="w-full h-10 rounded-xl border border-border/40 bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="input-ora"
              />
            </div>
          </div>
        </div>

        <Button
          className="w-full gap-2 rounded-xl h-11 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm"
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid="btn-invia-teleconsulto"
        >
          <Send className="w-4 h-4" />Invia Richiesta
        </Button>
      </motion.div>
    </div>
  );
}

function TeleconsultiPanel() {
  const [showModal, setShowModal] = useState(false);
  return (
    <div className="space-y-6" data-testid="teleconsulti-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Prossimi Teleconsulti</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{teleconsultiProgrammati.length} appuntamenti programmati</p>
        </div>
        <Button
          className="gap-2 rounded-xl h-10 px-5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm"
          onClick={() => setShowModal(true)}
          data-testid="btn-nuovo-teleconsulto"
        >
          <Plus className="w-4 h-4" />Richiedi nuovo Teleconsulto
        </Button>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="teleconsulti-grid">
        {teleconsultiProgrammati.map((tc, i) => {
          const st = statoTeleconsulto[tc.stato] || statoTeleconsulto.confermato;
          const isToday = tc.data === '19/02/2026';
          return (
            <motion.div key={tc.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Card className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden ${isToday ? 'ring-2 ring-violet-200' : ''}`} data-testid={`teleconsulto-card-${tc.id}`}>
                <CardContent className="p-0">
                  {/* Top accent bar */}
                  <div className={`h-1 ${tc.stato === 'confermato' ? 'bg-gradient-to-r from-violet-500 to-indigo-500' : 'bg-gradient-to-r from-amber-400 to-orange-400'}`} />

                  <div className="p-5 space-y-4">
                    {/* Date/time + Status */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex flex-col items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-violet-500 leading-none">{tc.data.split('/')[0]}</span>
                          <span className="text-[8px] text-violet-400 uppercase">
                            {['', 'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'][parseInt(tc.data.split('/')[1])]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />{tc.ora}
                          </p>
                          <p className="text-[10px] text-muted-foreground">{tc.data}</p>
                        </div>
                      </div>
                      <Badge className={`text-[9px] border ${st.bg} ${st.text} ${st.border}`}>{st.label}</Badge>
                    </div>

                    {/* Specialist info */}
                    <div className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-muted/20 border border-border/20">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-indigo-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground">{tc.specialista}</p>
                        <p className="text-[10px] text-muted-foreground">{tc.specialita}</p>
                      </div>
                    </div>

                    {/* Patient + Motivo */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground font-medium">Paziente:</span>
                        <span className="text-xs font-semibold text-foreground">{tc.paziente}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground italic">{tc.motivo}</p>
                    </div>

                    {/* CTA */}
                    <Button
                      className="w-full gap-2.5 rounded-xl h-11 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm"
                      onClick={() => toast.success('Videochiamata avviata', { description: `Connessione con ${tc.specialista} in corso...` })}
                      data-testid={`btn-videochiamata-${tc.id}`}
                    >
                      <Video className="w-4 h-4" />Partecipa alla Videochiamata
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Empty info card */}
      <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 text-blue-500" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-foreground">Teleconsulto d'emergenza</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Per teleconsulti urgenti non programmati, contatta la Centrale Operativa COT al numero dedicato oppure utilizza il canale "Centrale Operativa COT" nella Messaggistica Sicura.
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
        </CardContent>
      </Card>

      {/* Modal Nuovo Teleconsulto */}
      <AnimatePresence>
        {showModal && <NuovoTeleconsultoModal onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </div>
  );
}
export default function MMGCommunicationHub() {
  const [activeTab, setActiveTab] = useState('chat');
  const [activeThread, setActiveThread] = useState('thread-1');
  const [searchQuery, setSearchQuery] = useState('');
  const [localMessages, setLocalMessages] = useState(messaggiThread);

  const currentThread = threadsComunicazioni.find(t => t.id === activeThread);
  const currentMessages = localMessages[activeThread] || [];

  const handleSend = (testo) => {
    const newMsg = {
      id: `msg-new-${Date.now()}`,
      autore: 'Dr. Amato Luca',
      ruolo: 'MMG',
      testo,
      timestamp: new Date().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', ''),
      mio: true,
    };
    setLocalMessages(prev => ({
      ...prev,
      [activeThread]: [...(prev[activeThread] || []), newMsg],
    }));
    toast.success('Messaggio inviato');
  };

  return (
    <AppLayout role="mmg">
      <div className="space-y-5" data-testid="mmg-communication-hub">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground" data-testid="page-title">Comunicazioni</h1>
              <p className="text-sm text-muted-foreground">Hub centralizzato — Messaggistica e Telemedicina</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <TabSelector active={activeTab} onChange={setActiveTab} />

        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'chat' ? (
            <motion.div
              key="chat"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden" data-testid="chat-container">
                <div className="grid grid-cols-[320px_1fr] h-[calc(100vh-260px)] min-h-[500px]">
                  <ChatList
                    threads={threadsComunicazioni}
                    activeId={activeThread}
                    onSelect={setActiveThread}
                    searchQuery={searchQuery}
                    onSearch={setSearchQuery}
                  />
                  <ChatWindow
                    thread={currentThread}
                    messages={currentMessages}
                    onSend={handleSend}
                  />
                </div>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="teleconsulti"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <TeleconsultiPanel />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
